import { Carousel } from '@mantine/carousel'
import {
	Avatar,
	Badge,
	Box,
	Button,
	Card,
	Flex,
	Group,
	Image,
	Loader,
	Paper,
	Rating,
	ScrollArea,
	Tabs,
	Text,
	Title,
} from '@mantine/core'
import { IconMapPin } from '@tabler/icons-react'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Colors from '../../../constants/colors'
import { useResponsiveHooks } from '../../../hooks/useResponsiveHook'
import { getBusinessByIdAction } from '../../../store/business/businessesActions'
import { useAppDispatch, useAppSelector } from '../../../store/store'
import { notificationSystem } from '../../../utils/notificationSystem'
import ProductInfoCard from '../../buyer/product/ProductInfoCard'

const BusinessDetail = () => {
	const { isMobile, isLaptop } = useResponsiveHooks()
	const dispatch = useAppDispatch()
	const { singleBusiness, loaders } = useAppSelector((state) => state.business)
	const { id } = useParams()
	const navigate = useNavigate()

	useEffect(() => {
		if (!id) return
		dispatch(getBusinessByIdAction(id))
	}, [dispatch, id])

	return (
		<>
			{loaders.gettingSingleBusiness ? (
				<Flex h='100vh' w='100%' justify='center' align='center'>
					<Loader />
				</Flex>
			) : (
				<Flex direction={'column'}>
					<Flex></Flex>
					<Flex
						w={'100%'}
						justify={'space-between'}
						direction={isMobile || isLaptop ? 'column' : 'row'}
					>
						<Flex
							direction={'column'}
							py='md'
							w={isMobile || isLaptop ? '100%' : '65%'}
							style={{
								borderRadius: 12,
							}}
						>
							<Card radius='md' padding='sm' bg={Colors.grayBackground}>
								{/* Banner Image */}
								<Box>
									<Image
										src={singleBusiness?.businessInfo?.image}
										height={200}
										alt='Banner'
										style={{ objectFit: 'cover' }}
									/>
								</Box>

								{/* Profile Section */}
								<Box p='md'>
									<Flex
										justify='space-between'
										align={isMobile ? 'start' : 'center'}
										mb='sm'
										direction={isMobile ? 'column' : 'row'}
									>
										<Flex direction={'column'} gap={'xs'}>
											<Avatar
												src={singleBusiness?.businessInfo?.image}
												radius='xl'
												size='xl'
												styles={{
													root: { marginTop: -40, border: '2px solid white' },
												}}
											/>
											<Box>
												<Title order={3}>
													{singleBusiness?.businessInfo?.title}
												</Title>
												<Text size='sm' c='dimmed'>
													{singleBusiness?.businessInfo?.businessDescription}
												</Text>
												<Badge color='green' mt={4}>
													{singleBusiness?.businessInfo?.type}
												</Badge>
											</Box>
										</Flex>
										<Flex
											w={'100%'}
											gap={'xs'}
											direction={isMobile ? 'column-reverse' : 'row'}
											my={20}
										>
											<Button variant='outline' w={isMobile ? '100%' : '200px'}>
												Edit Business
											</Button>
											<Button
												color='green'
												onClick={() => {
													if (
														singleBusiness?.businessInfo?.verificationStatus ===
														'pending'
													) {
														notificationSystem(
															'Business Verification',
															'Your business verification is pending',
															true
														)
													} else {
														navigate(
															`/seller/add-product/${singleBusiness?.businessInfo?._id}`
														)
													}
												}}
											>
												Add Products
											</Button>
										</Flex>
									</Flex>

									{/* Address */}
									<Group gap={5} mt='xs'>
										<IconMapPin size={16} />
										<Text size='sm' c='dimmed'>
											{singleBusiness?.businessInfo?.address?.formattedAddress}
										</Text>
									</Group>

									{/* Stats and Tabs */}
									<Tabs defaultValue='business-info' mt='md'>
										<Tabs.List>
											<Tabs.Tab value='business-info'>Business Info</Tabs.Tab>
											<Tabs.Tab value='about-store'>About Store</Tabs.Tab>
										</Tabs.List>

										<Tabs.Panel value='business-info' pt='md'>
											<Group gap='xl' mt='sm'>
												<Box>
													<Text size='sm' color='dimmed'>
														Products
													</Text>
													<Text size='xl' fw={500}>
														20
													</Text>
												</Box>
												<Box>
													<Text size='sm' c='dimmed'>
														Sales
													</Text>
													<Text size='xl' fw={500}>
														70%
													</Text>
												</Box>
											</Group>
										</Tabs.Panel>

										<Tabs.Panel value='about-store' pt='md'>
											<Text c='dimmed'>
												This is where store information would go.
											</Text>
										</Tabs.Panel>
									</Tabs>
								</Box>
							</Card>

							{/* Contact Details */}
							<Paper radius='md' p='md' mt='md' bg={Colors.grayBackground}>
								<Group mb='xs'>
									<Box>
										<Text size='sm' color='dimmed'>
											Email address
										</Text>
										<Text>{singleBusiness?.businessInfo?.email}</Text>
									</Box>
									<Box>
										<Text size='sm' color='dimmed'>
											Contact
										</Text>
										<Text>{singleBusiness?.businessInfo?.phoneNumber}</Text>
									</Box>
								</Group>

								<Group>
									<Box>
										<Text size='sm' c='dimmed'>
											Registration No
										</Text>
										<Text>
											{singleBusiness?.businessInfo?.registrationNumber}
										</Text>
									</Box>
									<Box>
										<Text size='sm' color='dimmed'>
											Category
										</Text>
										<Text>{singleBusiness?.businessInfo?.category}</Text>
									</Box>
								</Group>
							</Paper>

							{/* products */}

							<Flex
								align={'center'}
								justify={'space-between'}
								my={10}
								mt={30}
								p={'sm'}
							>
								<Title order={2} fw={'normal'}>
									Products
								</Title>
								<Text
									style={{
										cursor: 'pointer',
									}}
									c={Colors.primaryColor}
								>
									View All
								</Text>
							</Flex>
							<Carousel slideSize='20.333333%' align={'start'}>
								{singleBusiness?.latestProducts.map((product, index) => {
									return (
										<Carousel.Slide key={index}>
											<ProductInfoCard product={product} />
										</Carousel.Slide>
									)
								})}
							</Carousel>
						</Flex>

						<Flex
							w={isMobile || isLaptop ? '100%' : '30%'}
							direction='column'
							p='md'
							bg={Colors.grayBackground}
							style={{
								borderRadius: 12,
							}}
							h={'100%'}
						>
							<Title order={3}>Customer Feedback</Title>
							<ScrollArea h={400}>
								{singleBusiness?.reviews?.reviews?.map((review, index) => {
									return (
										<Group
											key={index}
											p='sm'
											bg='white'
											gap='sm'
											my={'sm'}
											style={{ borderRadius: 12 }}
										>
											<Box h={60} w={60}>
												<Image
													src={review?.userPersonalInformation?.profilePic}
													h={'100%'}
													w={'100%'}
													style={{
														borderRadius: 12,
													}}
												/>
											</Box>
											<Box style={{ flex: 1 }}>
												<Text fw={600}>
													{review?.userPersonalInformation?.firstName}
												</Text>
												<Group>
													<Rating
														value={review?.rating}
														readOnly
														c={Colors.ratingStar}
													/>{' '}
													<Text c={Colors.secondaryText}>
														{review?.rating} / 5
													</Text>
												</Group>
											</Box>
										</Group>
									)
								})}
							</ScrollArea>
						</Flex>
					</Flex>
				</Flex>
			)}
		</>
	)
}

export default BusinessDetail
